import React from 'react'
import { View } from 'react-native'
import { startCase } from 'lodash'
import { ScrollView } from 'react-native-gesture-handler'
import { useTheme } from '@ui-kitten/components'
import { useIsDarkMode } from '@src/config/theme'
import { StyleService, useStyleSheet } from '@style/service'
import { ChatbotConversationTag } from '@src/types'
import { tagColors } from './ConversationTag'
import { ConversationFilterPill } from './ConversationFilterPill'

export type ConversationFilter = 'all' | 'pinned' | 'reports' | ChatbotConversationTag
type ConversationColorTag = keyof typeof tagColors

interface ConversationFilterPillsProps {
  selectedFilter: ConversationFilter
  onFilterChange: (filter: ConversationFilter) => void
}

export const ConversationFilterPills = ({
  selectedFilter,
  onFilterChange,
}: ConversationFilterPillsProps) => {
  const styles = useStyleSheet(themedStyle)
  const theme = useTheme()
  const isDarkMode = useIsDarkMode()

  return (
    <View style={styles.container}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.content}
      >
        <ConversationFilterPill
          label="All"
          isActive={selectedFilter === 'all'}
          onPress={() => onFilterChange('all')}
          activeBackgroundColor={theme['theme.text.primary']}
          inactiveBackgroundColor={theme['theme.surface.base1']}
        />
        <ConversationFilterPill
          label="Reports"
          isActive={selectedFilter === 'reports'}
          onPress={() => onFilterChange('reports')}
          activeBackgroundColor={theme['theme.text.secondary']}
          inactiveBackgroundColor={theme['theme.surface.base2']}
        />
        <ConversationFilterPill
          label="Pinned"
          isActive={selectedFilter === 'pinned'}
          onPress={() => onFilterChange('pinned')}
          activeBackgroundColor={theme['theme.text.secondary']}
          inactiveBackgroundColor={theme['theme.surface.base']}
        />
        {(Object.keys(tagColors) as ConversationColorTag[]).map((tag) => {
          const colors = tagColors[tag]
          const lightBg = theme[colors.background]
          const inactiveBg = isDarkMode ? `${String(lightBg)}1A` : lightBg
          const textColor = theme[isDarkMode ? colors.darkText : colors.text]

          return (
            <ConversationFilterPill
              key={tag}
              label={startCase(tag)}
              isActive={selectedFilter === tag}
              onPress={() => onFilterChange(tag)}
              activeBackgroundColor={textColor}
              inactiveBackgroundColor={inactiveBg}
              inactiveTextColor={textColor}
            />
          )
        })}
      </ScrollView>
    </View>
  )
}

const themedStyle = StyleService.create({
  container: {
    marginBottom: 8,
  },
  content: {
    paddingHorizontal: 16,
    gap: 8,
  },
})
